import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { ObservableScope } from '../models/observable-scope.enum';
import { ConfigParserService } from '../services/config-parser.service';
import { EventService } from '../services/event.service';
import { DOMInteractiveMarker } from '../utilities/dom-interactive-marker.utility';
import { AngularEvents } from './angular-events';
import { CapabilitiesService } from './capabilities.service';
import { AuthType, ClientStartupConfigService } from './client-startup-config.service';

export interface UserConsentConfig {
    userConsent: ConsumerUserConsentGroup;
    status: ConsumerUserConsentDataStatus;
}

export const enum ConsumerUserConsentGroup {
    CanConsent,
    PartialConsent,
    CannotConsent
}

const enum ConsumerUserConsentDataStatus {
    DataPresent,
    DataNotPresent,
    ServiceFailure
}

@Injectable({
    providedIn: 'root'
})
export class UserConsentConfigService {
    private configPromise: Promise<UserConsentConfig>;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly configParserService: ConfigParserService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly eventService: EventService
    ) {
    }

    public getConfig(): Promise<UserConsentConfig> {
        if (!this.configPromise) {
            if (this.startupConfigService.config.authType !== AuthType.MSA) {
                return Promise.reject();
            }

            if (this.capabilitiesService.capabilities.isPwaInUwp) { // if PWA, we turn off all features
                this.configPromise = Promise.resolve(this.configParserService.parse<UserConsentConfig>('consumerUserConsent'));
            } else {
                const tokensLoadedPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.primaryTokensLoaded),
                    take(1))
                    .toPromise();
                const domReadyPromise = DOMInteractiveMarker.fullPageReady();
                this.configPromise = Promise.race([tokensLoadedPromise, domReadyPromise]).then(() => this.configParserService.parse<UserConsentConfig>('consumerUserConsent'));
            }
        }
        return this.configPromise;
    }
}
